import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'

export type RubricRevisionSummary = {
  id: string
  version: number
  provenance: string
  created_at: string
}

export type RubricCriterion = {
  key: string
  kind: string
  label: string
  definition?: string
  weight?: number | null
  scale?: Record<string, unknown>
  levels?: Record<string, unknown>
  metadata?: Record<string, unknown>
  component_skills?: string[]
  sort_order?: number
}

export type RubricRevisionDetail = {
  id: string
  version: number
  payload: Record<string, unknown>
  provenance: string
  criteria: RubricCriterion[]
}

export type RubricCorpus = {
  id: string
  slug: string
  label: string
  domain_key: string
  methodology_id: string
  status: string
  current_revision: RubricRevisionDetail | null
}

export type RubricCorpusDetail = RubricCorpus & {
  revisions: RubricRevisionSummary[]
  current_revision: RubricRevisionDetail | null
}

export type PublishRubricResponse = {
  id: string
  version: number
  payload: Record<string, unknown>
  provenance: string
  stale_datums_marked: number
}

export type RubricMethodology = {
  methodology_id: string
  label: string
  default_domain_key: string
  description: string
}

export type PublicationStatus = 'draft' | 'published'

/** Talent-pool roll-up a pack may send on a skill or skill domain. */
export type SkillPoolRollup = {
  candidate_count?: number
  /** Proficiency label → candidate count, in the pack's display order. */
  proficiency_mix?: Record<string, number>
  /** Skills only: 1 = most possessed skill, out of `pool_size` skills; percentile is 0–100. */
  pool_rank?: number
  pool_size?: number
  pool_percentile?: number
}

export type SkillDomainSkill = {
  id: string
  name: string
  status?: 'approved' | 'crowdsourced'
  aliases?: string[]
  publication_status?: PublicationStatus
} & SkillPoolRollup

export type SkillDomainSummary = {
  id: string
  key: string
  label: string
  domain_key: string
  parent_id?: string | null
  skill_count: number
  skill_ids?: string[]
  publication_status?: PublicationStatus
  /** When present the page lists skills without fetching each domain. */
  skills?: SkillDomainSkill[]
} & Pick<SkillPoolRollup, 'candidate_count' | 'proficiency_mix'>

export type SkillDomainDetail = SkillDomainSummary & {
  skills: SkillDomainSkill[]
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json()
  if (!response.ok) {
    const detail = (data as { detail?: string; error?: string }).detail
      || (data as { error?: string }).error
      || response.statusText
    throw new Error(detail)
  }
  return data as T
}

export function useCurrentRubric() {
  return useQuery({
    queryKey: ['admin-rubric-current'],
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/evaluations/rubrics/current`)
      return parseJson<RubricCorpusDetail>(response)
    },
  })
}

export function useRubricCorpora(methodologyId?: string) {
  const query = methodologyId ? `?methodology_id=${encodeURIComponent(methodologyId)}` : ''
  return useQuery({
    queryKey: ['admin-rubric-corpora', methodologyId ?? 'all'],
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/evaluations/rubrics${query}`)
      return parseJson<RubricCorpus[]>(response)
    },
  })
}

export function useRubricCorpus(corpusId: string | undefined) {
  return useQuery({
    queryKey: ['admin-rubric-corpus', corpusId],
    enabled: Boolean(corpusId),
    queryFn: async () => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/rubrics/corpora/${corpusId}`,
      )
      return parseJson<RubricCorpusDetail>(response)
    },
  })
}

export function useRubricMethodologies() {
  return useQuery({
    queryKey: ['admin-rubric-methodologies'],
    queryFn: async () => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/rubrics/methodologies`,
      )
      return parseJson<RubricMethodology[]>(response)
    },
  })
}

export function useCreateRubricCorpus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      methodology_id: string
      label: string
      slug_suffix?: string
      domain_key?: string
    }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/evaluations/rubrics/corpora`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      return parseJson<RubricCorpusDetail>(response)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-rubric-corpora'] })
    },
  })
}

export function useDuplicateRubricCorpus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      corpusId,
      label,
      slugSuffix,
    }: {
      corpusId: string
      label?: string
      slugSuffix?: string
    }) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/rubrics/corpora/${corpusId}/duplicate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: label ?? '',
            slug_suffix: slugSuffix ?? 'copy',
          }),
        },
      )
      return parseJson<RubricCorpusDetail>(response)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-rubric-corpora'] })
    },
  })
}

export function useDraftRubricCriteria() {
  return useMutation({
    mutationFn: async (body: {
      natural_language: string
      methodology_id?: string
      kind_hint?: string
    }) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/rubrics/draft-criteria`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      return parseJson<{ proposed_criteria: RubricCriterion[]; note?: string }>(response)
    },
  })
}

export function useSkillDomains(domainKey = 'employment') {
  return useQuery({
    queryKey: ['admin-skills-catalog', domainKey],
    queryFn: async () => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/skills/domains?domain_key=${encodeURIComponent(domainKey)}`,
      )
      return parseJson<SkillDomainSummary[]>(response)
    },
  })
}

export function useSkillDomain(domainId: string | undefined) {
  return useQuery({
    queryKey: ['admin-skills-domain', domainId],
    enabled: Boolean(domainId),
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/domains/${domainId}`)
      return parseJson<SkillDomainDetail>(response)
    },
  })
}

function invalidateSkillLexicon(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['admin-skills-catalog'] })
  void queryClient.invalidateQueries({ queryKey: ['admin-skills-domain'] })
  void queryClient.invalidateQueries({ queryKey: ['admin-skills-proposals'] })
  void queryClient.invalidateQueries({ queryKey: ['skills'] })
}

export function useSetSkillPublicationStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      kind,
      id,
      publicationStatus,
    }: {
      kind: 'domain' | 'skill'
      id: string
      publicationStatus: PublicationStatus
    }) => {
      const base = kind === 'domain' ? `/api/skills/domains/${id}` : `/api/skills/${id}`
      const action = publicationStatus === 'published' ? 'publish' : 'draft'
      const response = await authorizedFetch(`${API_ORIGIN}${base}/${action}`, { method: 'POST' })
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export type LexiconProposal = {
  id: string
  kind: string
  skill_name?: string | null
  canonical_name?: string | null
  alias_text?: string | null
  confidence?: number | null
  evidence?: Record<string, unknown>
}

export function useLexiconProposals() {
  return useQuery({
    queryKey: ['admin-skills-proposals'],
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/proposals`)
      return parseJson<LexiconProposal[]>(response)
    },
  })
}

export function useApproveSkill() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (skillId: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/${skillId}/approve`, {
        method: 'POST',
      })
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useAddSkillAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ skillId, alias }: { skillId: string; alias: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/${skillId}/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias }),
      })
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useRemoveSkillAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (alias: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/aliases/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias }),
      })
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useCreateApprovedSkill() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/approved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      return parseJson<{ id: string; name: string }>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useCreateSkillDomain() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { label: string; domain_key?: string; sort_order?: number }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return parseJson<{ id: string; label: string }>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useAddDomainMembership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ domainId, skillId }: { domainId: string; skillId: string }) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/skills/domains/${domainId}/memberships`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skill_id: skillId }),
        },
      )
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useMergeSkills() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      duplicateSkillId,
      canonicalSkillId,
    }: {
      duplicateSkillId: string
      canonicalSkillId: string
    }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duplicate_skill_id: duplicateSkillId,
          canonical_skill_id: canonicalSkillId,
          confirm: true,
        }),
      })
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useApplyLexiconProposal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/skills/proposals/${proposalId}/apply`,
        { method: 'POST' },
      )
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useDismissLexiconProposal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/skills/proposals/${proposalId}/dismiss`,
        { method: 'POST' },
      )
      return parseJson<unknown>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useCatalogImportPreview() {
  return useMutation({
    mutationFn: async ({ csv, domainKey = 'employment' }: { csv: string; domainKey?: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/import/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, domain_key: domainKey }),
      })
      return parseJson<{
        ok: boolean
        change_count: number
        conflict_count: number
        changes: unknown[]
        conflicts: unknown[]
      }>(response)
    },
  })
}

export function useCatalogImportApply() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ csv, domainKey = 'employment' }: { csv: string; domainKey?: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/skills/import/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, domain_key: domainKey }),
      })
      return parseJson<{ ok: boolean; applied_count: number }>(response)
    },
    onSuccess: () => invalidateSkillLexicon(queryClient),
  })
}

export function useCatalogExport() {
  return useMutation({
    mutationFn: async ({ domainKey = 'employment' }: { domainKey?: string } = {}) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/skills/export?domain_key=${encodeURIComponent(domainKey)}`,
      )
      return parseJson<{ domain_key: string; csv: string }>(response)
    },
  })
}

export function usePublishRubricRevision() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      corpusId,
      payload,
      changeSummary,
    }: {
      corpusId: string
      payload: Record<string, unknown>
      changeSummary: string
    }) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/rubrics/${corpusId}/revisions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload, change_summary: changeSummary }),
        },
      )
      return parseJson<PublishRubricResponse>(response)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-rubric-corpora'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-rubric-current'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-rubric-corpus', variables.corpusId] })
    },
  })
}

export type RubricPreviewResponse = {
  payload: import('@/hooks/use-evaluation-analysis').EvaluationAnalysisPayload
  preview_meta?: {
    subject_label?: string
    profile_id?: string | null
    opportunity_id?: string | null
    opportunity_title?: string
    application_id?: string | null
    evaluation_id?: string | null
    methodology_id?: string
    condition_id?: string | null
  }
}

export function useRubricPreviewRun() {
  return useMutation({
    mutationFn: async ({
      corpusId,
      payload,
    }: {
      corpusId: string
      payload: Record<string, unknown>
    }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/evaluations/rubrics/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ corpus_id: corpusId, payload }),
      })
      return parseJson<RubricPreviewResponse>(response)
    },
  })
}

export function criterionKindLabel(kind: string): string {
  if (kind === 'competency') return 'Focus area'
  if (kind === 'dimension') return 'Dimension'
  if (kind === 'requirement') return 'Requirement'
  return kind
}
